import React, { ReactElement, useMemo, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { BarChart3 } from 'lucide-react';
import { useListSprints } from '@data/hooks/sprints';
import { useOrganizationId } from '@app/utils/hooks';
import { toFixedHours } from '@app/utils/helpers/date-utils';
import { Card } from '@app/components/core';
import { Skeleton } from '@ui/components/ui/skeleton';
import { StackedBarChart } from '../clientReporting/product/charts';
import ProductPicker from './ProductPicker';
import {
  MAX_LINES,
  ProductSeries,
  SprintWindow,
  sprintSeries,
  hoursCompact,
  restSeries,
  useProductHoursBySprint,
  withDistinctColors,
  yearSprintWindows,
} from './productTime';

/** Hours per product in each of this year's sprints, one stacked bar per sprint. */
export const TimePerProductCard = (): ReactElement => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId() ?? '';

  const { data: sprintsData, isLoading: sprintsLoading } = useListSprints({ limit: 200 });
  const windows = useMemo(() => yearSprintWindows(sprintsData?.items ?? [], new Date()), [sprintsData]);
  const { bySprint, isLoading: hoursLoading, isError } = useProductHoursBySprint(organizationId, windows);
  // The sprints have to land before there is a window to ask for, and until they do there are no queries to
  // be loading — so without this the card paints "nothing logged this year" before it has asked anything.
  const isLoading = sprintsLoading || hoursLoading;

  const allSeries = useMemo(() => sprintSeries(bySprint), [bySprint]);

  // `null` = the manager has picked nothing yet, so the default follows the data: the busiest products, which
  // is what `sprintSeries` already ranks by.
  const [picked, setPicked] = useState<string[] | null>(null);
  const selected = picked ?? allSeries.slice(0, MAX_LINES).map(s => s.key);
  const pickedSeries = withDistinctColors(allSeries.filter(s => selected.includes(s.key)));
  const rest = restSeries(allSeries, selected, t('otherProducts'));
  // Biggest over the year first, so it sits at the bottom of every bar; the rest is ranked like any product.
  const series = (rest ? [...pickedSeries, rest] : pickedSeries).sort((a, b) => b.total - a.total);

  return (
    <Card className="flex flex-col">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-base font-semibold text-foreground">
          <BarChart3 className="size-4 text-muted-foreground" />
          {t('timePerProductPerSprint')}
        </p>
        {allSeries.length > 0 && (
          <ProductPicker options={allSeries} value={selected} onChange={setPicked} max={MAX_LINES} />
        )}
      </div>

      {(() => {
        if (isLoading) return <Skeleton className="h-52 w-full rounded-lg" />;
        // Never a clean-looking empty chart on a failed read: no card is allowed to report a result it does
        // not have.
        if (isError) {
          return (
            <p className="flex h-52 items-center justify-center rounded-xl border border-dashed border-border px-4 text-center text-sm text-muted-foreground">
              {t('cardReadFailed')}
            </p>
          );
        }
        if (!allSeries.length) {
          return (
            <p className="flex h-52 items-center justify-center rounded-xl border border-dashed border-border px-4 text-center text-sm text-muted-foreground">
              {t('noBillableTimeThisYear')}
            </p>
          );
        }

        // eslint-disable-next-line no-use-before-define
        return <SprintBars series={series} windows={windows} />;
      })()}

      <p className="mt-4 text-xs text-muted-foreground">{t('timeTrendCaption')}</p>
    </Card>
  );
};

/**
 * One stacked bar per sprint, a segment per product holding that sprint's hours alone. The legend doubles as
 * a toggle, as the client dashboard's chapter breakdown does.
 */
const SprintBars = ({ series, windows }: { series: ProductSeries[]; windows: SprintWindow[] }): ReactElement => {
  const { t } = useTranslation();
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string): void =>
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);

      return next;
    });

  const bars = series.map(s => ({ key: s.key, label: s.label, color: s.color, data: s.perSprint }));

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
      <div className="flex min-w-0 flex-1 items-center">
        <StackedBarChart
          series={bars}
          labels={windows.map(w => String(w.number))}
          hiddenKeys={hidden}
          tip={(seriesIndex, barIndex, value) =>
            `${t('sprintCount', { count: windows[barIndex]?.number ?? 0 })} · ${bars[seriesIndex].label}: ${t(
              'nHours',
              {
                hours: toFixedHours(value),
              },
            )}`
          }
          formatTick={hoursCompact}
          heightClass="h-52"
          className="w-full"
        />
      </div>

      <div className="lg:w-56 lg:shrink-0 lg:border-l lg:border-border lg:pl-6">
        <ul className="space-y-0.5">
          {series.map(s => {
            const isHidden = hidden.has(s.key);

            return (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => toggle(s.key)}
                  aria-pressed={!isHidden}
                  className={cx(
                    'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition duration-200 hover:bg-muted',
                    isHidden && 'opacity-45',
                  )}
                >
                  {/* Filled when shown, hollow when hidden, so the state never rests on colour alone. */}
                  <span
                    aria-hidden
                    className="size-3 shrink-0 rounded-[3px] transition-all duration-200"
                    style={{
                      backgroundColor: isHidden ? 'transparent' : s.color,
                      boxShadow: `inset 0 0 0 1.5px ${s.color}`,
                    }}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-foreground">{s.label}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{hoursCompact(s.total)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
